import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useTRPC, type RouterOutputs } from "@/trpc";
import type { Credential, CredentialsResult, VaultFile } from "../types";
import { VaultFilesView } from "../components/vault-files/vault-files-view";
import { PendingUploadsNotice } from "../components/pending-uploads-notice";
import {
  vaultFilesQueryOptions,
  type CredentialVaultScope,
} from "../lib/credential-vault-scope";
import { credentialReviewTarget } from "../lib/credential-review-target";
import type { useCredentialDocumentPreview } from "./use-credential-document-url";
import { useVaultFileDownloads } from "./use-vault-file-downloads";

/** Connect file browsing to the vault's existing preview and credential editor. */
export function useCredentialVaultFiles({
  scope,
  canEdit = true,
  data,
  reviewItems,
  refreshReviewItems,
  preview,
  uploadDisabled,
  onReviewCredential,
}: {
  scope: CredentialVaultScope;
  canEdit?: boolean;
  data: CredentialsResult | null;
  reviewItems: RouterOutputs["credentials"]["listMyCandidates"] | undefined;
  refreshReviewItems: () => Promise<unknown>;
  preview: ReturnType<typeof useCredentialDocumentPreview>;
  uploadDisabled: boolean;
  onReviewCredential: (credential: Credential, documentId: string) => void;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const downloads = useVaultFileDownloads({ scope });
  const [uploadState, setUploadState] = useState({
    isUploading: false,
    pendingFileCount: 0,
  });
  const [reviewDocumentId, setReviewDocumentId] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<VaultFile | null>(null);

  useEffect(() => {
    if (canEdit) {
      return;
    }
    setReviewDocumentId(null);
    setUploadState({ isUploading: false, pendingFileCount: 0 });
  }, [canEdit]);

  async function refresh() {
    await queryClient.invalidateQueries({
      queryKey: vaultFilesQueryOptions(trpc, scope).queryKey,
    });
  }

  function openReview(credential: Credential, documentId: string) {
    setReviewDocumentId(null);
    onReviewCredential(credential, documentId);
  }

  function reviewFile(file: VaultFile) {
    if (
      !canEdit ||
      !data ||
      !file.canDownload ||
      file.reviewState === "reading"
    ) {
      return;
    }
    setPreviewFile(file);
    const item = reviewItems?.find((row) => row.documentId === file.id);
    if (!item) {
      toast.error(
        "This upload has changed. Refreshing your files; please try again.",
      );
      void refreshReviewItems();
      void refresh();
      return;
    }
    const target = credentialReviewTarget({
      item,
      credentials: data.credentials,
    });
    if (target && item.state === "needs-review") {
      openReview(target, file.id);
    } else {
      setReviewDocumentId(file.id);
    }
  }

  function resetFileContext() {
    setReviewDocumentId(null);
    setPreviewFile(null);
  }

  const view = data ? (
    <VaultFilesView
      scope={scope}
      canUpload={canEdit}
      categories={data.categories}
      downloads={downloads}
      uploadDisabled={uploadDisabled}
      onUploadStateChange={setUploadState}
      onPreview={(file) => {
        setPreviewFile(file);
        if (file.isReviewDocument) {
          void preview.openReview(file);
        } else {
          void preview.open(file);
        }
      }}
      reviewDocumentId={reviewDocumentId}
      reviewContent={
        canEdit && reviewDocumentId ? (
          <div className="space-y-2">
            <div className="flex justify-end">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReviewDocumentId(null)}
              >
                Close review
              </Button>
            </div>
            <PendingUploadsNotice
              appearance="profile"
              items={(reviewItems ?? []).filter(
                (item) => item.documentId === reviewDocumentId,
              )}
              credentials={data.credentials}
              categories={data.categories}
              onReview={openReview}
              onPreview={preview.openReview}
            />
          </div>
        ) : null
      }
    />
  ) : null;

  return {
    view,
    isBusy:
      uploadState.isUploading ||
      uploadState.pendingFileCount > 0 ||
      downloads.isBusy,
    isPreviewDownloading:
      previewFile?.id === preview.document?.id && downloads.isBusy,
    downloadPreview: () => {
      if (previewFile && previewFile.id === preview.document?.id) {
        void downloads.downloadFile(previewFile);
      } else {
        void preview.download();
      }
    },
    reviewPreview:
      canEdit &&
      previewFile &&
      previewFile.id === preview.document?.id &&
      previewFile.isReviewDocument &&
      previewFile.canDownload &&
      previewFile.reviewState !== "reading"
        ? () => {
            preview.close();
            reviewFile(previewFile);
          }
        : undefined,
    closePreview: () => {
      setPreviewFile(null);
      preview.close();
    },
    refresh,
    onCredentialSaved: () => {
      resetFileContext();
      void refresh();
    },
    close: () => {
      resetFileContext();
      downloads.dismissFailures();
    },
  };
}
